import * as React from "react";

import { Box, Typography } from "@material-ui/core";

import { OnboardingStatusContext } from "client/context";
import {
  OnboardingStatusToCurrentStateMap,
  Status,
  StatusLabel,
  StatusMap,
  StatusStep,
  useStyles,
} from "client/domain/OnboardingStatus";
import {
  useBranding,
  useOnboardingStatusContext,
  useRunOnce,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";

const onboardingStatusToCurrentStateMap: OnboardingStatusToCurrentStateMap = {
  INELIGIBLE: "Declined",
  NO_RECORD: "None",
  IN_PROGRESS: "None",
  IN_REVIEW: "None",
  REQUIRES_ATTENTION: "RequiresAttention",
  READY_TO_INSTALL: "InProgress",
  COMPLETE: "InProgress",
};

export const determineCurrentState: (
  params: Pick<
    OnboardingStatusContext,
    "inStoreDirectDebitAuthorized" | "onboardingStatus" | "opportunityStage"
  >
) => Status = ({
  inStoreDirectDebitAuthorized,
  onboardingStatus,
  opportunityStage,
}) => {
  if (onboardingStatus === "INELIGIBLE") {
    return "Declined";
  }

  if (onboardingStatus === "REQUIRES_ATTENTION") {
    return "Paused";
  }

  if (!inStoreDirectDebitAuthorized) {
    return "None";
  }

  if (opportunityStage === "Implemented" || opportunityStage === "Closed Won") {
    return "Done";
  }

  return onboardingStatusToCurrentStateMap[onboardingStatus];
};

export const ActivateInStoreEmailStep: React.FC = () => {
  const { brandTitle } = useBranding();
  const classes = useStyles();
  const {
    inStoreDirectDebitAuthorized,
    onboardingStatus,
    opportunityStage,
  } = useOnboardingStatusContext();

  const currentState = determineCurrentState({
    inStoreDirectDebitAuthorized,
    onboardingStatus,
    opportunityStage,
  });

  const statusMap: StatusMap = {
    None: {},
    InProgress: {
      className: classes.light,
    },
    Done: {
      className: classes.dark,
    },
    Paused: {},
    Reviewing: {
      className: classes.light,
    },
    RequiresAttention: {},
    Declined: {},
  };

  const { className } = statusMap[currentState];

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_SCREEN", "activate_instore_email_card_loaded")
  );

  return (
    <StatusStep
      timeToFade={500}
      activeOnboardingStep="ActivateInStore"
      stepStatus={currentState}
    >
      <Box className={`${className} ${classes.statusWrapper}`} mb={2} p={2}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>Activate {brandTitle} in-store</strong>
          </Typography>
          <StatusLabel
            status={
              // Right now we don't have a way to tell when they have responded to the
              // email so for right now if this step is at all available we show the
              // 'respond to email' text in the status label
              currentState === "InProgress" ? "RequiresAttention" : currentState
            }
          />
        </Box>
        {currentState === "InProgress" && (
          <Box
            mt={1}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Box width="75%">
              <Typography>
                We’ve emailed you the steps for activating {brandTitle} on your
                in-store payment terminal. Please check your inbox.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
